import React, { useState, useEffect } from "react";
import classNames from "classnames";
import { UpArrowIcon, DownArrowIcon } from "../icons";
import "./dropDownGlow.scss";

const DropDwnGlow = ({
  options,
  selectedOption,
  onChange,
  placeholder,
  isOpen,
  setOpen,
  className,
  error,
  onFocus,
  onClick, // optional onClick prop from parent
}) => {
  const [currentSelection, setCurrentSelection] = useState(placeholder);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [glow, setGlow] = useState(false);

  // Trigger glow effect and toggle dropdown
  const handleGlowEffect = (e) => {
    if (onClick) {
      onClick(e);
    }
    setGlow(true);
    // Remove glow after 2 seconds and toggle open state
    setTimeout(() => {
      setGlow(false);
      setOpen(!isOpen);
    }, 2000);
  };

  // Handle selecting an option
  const handleShowChoice = (e, option) => {
    e.stopPropagation();
    setOpen(false);
    setCurrentSelection(option.label);
    // Wrap the value in an object that mimics an event:
    onChange({ target: { value: option.value } });
    setHighlightedIndex(-1);
  };
  
  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prevIndex) =>
        prevIndex < options.length - 1 ? prevIndex + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : options.length - 1
      );
    } else if (e.key === "Enter" && highlightedIndex !== -1) {
      e.preventDefault();
      handleShowChoice(e, options[highlightedIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const handleMouseEnter = (index) => setHighlightedIndex(index);
  const handleMouseLeave = () => setHighlightedIndex(-1);

  // Close dropdown if clicking outside
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!event.target.closest(".DropDwnGlowContainer")) {
        setOpen(false);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [setOpen]);

  // Update current selection when selectedOption changes
  useEffect(() => {
    if (selectedOption) {
      const selected = options.find(
        (option) => option.value === selectedOption
      );
      setCurrentSelection(selected ? selected.label : placeholder);
    } else {
      setCurrentSelection(placeholder);
    }
  }, [selectedOption, placeholder, options]);

  const handleFocus = () => {
    if (onFocus) onFocus();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setCurrentSelection(placeholder);
    onChange({ target: { value: "" } });
  };

  // Use classNames to conditionally apply glow classes
  const containerClasses = classNames("DropDwnGlowContainer", className, {
    "glow-on-animation": glow,
    "show-animation": glow,
  });

  return (
    <div
      className={containerClasses}
      onClick={handleGlowEffect}
      onKeyDown={handleKeyDown}
      tabIndex="0"
      onFocus={handleFocus}
    >
      <div className="glow-input-wrapper">
        <div className={`divTextBox ${error ? "error-message-style" : ""}`}>
          {error || currentSelection}
        </div>
        <div className="glow-payment-dropdown-icon">
          {isOpen ? (
            <UpArrowIcon
              f
              fillColor="var(--color-bloodred)"
              width="11.906"
              height="15.592"
            />
          ) : (
            <DownArrowIcon
              fillColor="var(--color-bloodred)"
              width="11.906"
              height="15.592"
            />
          )}
        </div>
      </div>
      <div className={`options ${isOpen ? "open" : "close"}`}>
        {options.map((option, index) => (
          <div
            key={index}
            onClick={(e) => handleShowChoice(e, option)}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
            className={classNames("option", {
              "active-dropdown": option.value === selectedOption,
              "highlighted-option": highlightedIndex === index,
            })}
          >
            {option.label}
          </div>
        ))}
      </div>
      {selectedOption && (
        <button
          type="button"
          onClick={handleClear}
          className="clear-payment-method-btn"
        >
          Clear
        </button>
      )}
    </div>
  );
};

export default DropDwnGlow;
